// Состояние входа в SPA: кто вошёл (me), что ему можно (capabilities), какой экран показать (вход, регистрация,
// первичная настройка, смена временного пароля, приложение), потеря сессии посреди работы (повторный вход без
// потери текста), отчёт другого пользователя, открытый администратором.
//   startAuth(expose) - запуск: если страница уже с токеном сессии (сервер подставил) - сразу приложение;
//   authStore / useAuth - состояние; can('canOpenWord') - возможность; authApi - запросы /api/auth/*.
// Контракт сервера - web/ARCHITECTURE.md §10.3, web/server/stoweb/routes/auth.py.
import { createStore, useStore } from 'zustand';
import { api, ApiError, authHooks, getApiToken, setApiToken } from '../../core/api';
import { registry } from '../../core/registry';

export interface Caps {
  admin: boolean; local: boolean; canOpenWord: boolean; canReveal: boolean; canPasteLocalFiles: boolean;
  word: boolean; canManageUsers: boolean;
}
export interface MeUser {
  id: number; login: string; name: string; role: 'admin' | 'user'; blocked: boolean; mustChangePassword: boolean;
  created: number; lastLogin: number | null;
}
/** Подписка: active - можно работать; exempt - администратор (подписка не нужна). */
export interface Subscription { active: boolean; lifetime: boolean; until: number | null; exempt: boolean }
export interface Me {
  user: MeUser; csrf: string; capabilities: Caps;
  quota: { usedBytes: number; quotaBytes: number | null; maxFileBytes: number | null; maxProjects: number | null; projects: number };
  session: { remember: boolean; expires: number };
  subscription: Subscription;
  telegram: { linked: boolean; username: string };
}
export interface AuthInfo {
  needsSetup: boolean; setupAllowed: boolean; setupCodeRequired?: boolean; registration: 'invite' | 'open' | 'closed'; inviteRequired: boolean;
  registrationOpen: boolean; inviteValid?: boolean; passwordRules: { minLength: number; loginPattern: string; text: string };
  local: boolean; legacyProjects?: number; user: Me | null;
  localUrl?: string;   // только пока нет пользователей: адрес редактора на компьютере-сервере
  setupCodeFile?: string;   // только пока нет пользователей и только на этом компьютере: файл с кодом первого запуска
}
export interface Owner { id: number; login: string; name: string }
export interface SessionRow { id: string; current: boolean; created: number; lastSeen: number; expires: number; remember: boolean; ip: string; ua: string }

export type Phase = 'boot' | 'anon' | 'change' | 'app' | 'fatal';
export type AnonView = 'login' | 'register' | 'setup' | 'noSetup' | 'reset';

export interface AuthState {
  phase: Phase;
  view: AnonView;
  me: Me | null;
  info: AuthInfo | null;
  /** код приглашения из ссылки /login?invite=… */
  invite: string;
  /** ник пригласившего из ссылки /login?ref=… */
  ref: string;
  /** код первого запуска из адреса /login?setup=… (сервер сам открывает страницу с ним) */
  setupCode: string;
  /** пароль, которым только что вошли (для формы смены временного пароля; только в памяти) */
  lastPassword: string;
  /** сессия потеряна посреди работы: показать повторный вход поверх редактора; reason - почему (сервер, 401) */
  lost: { at: number; reason?: string } | null;
  /** открыт отчёт другого пользователя (администратор) */
  foreign: { pid: string; owner: Owner } | null;
  error: string;
}

export const authStore = createStore<AuthState>()(() => ({
  phase: 'boot', view: 'login', me: null, info: null, invite: '', ref: '', setupCode: '', lastPassword: '', lost: null, foreign: null, error: '',
}));
export function useAuth<T>(sel: (s: AuthState) => T): T { return useStore(authStore, sel); }
const set = (p: Partial<AuthState>) => authStore.setState(p);

const NO_CAPS: Caps = { admin: false, local: false, canOpenWord: false, canReveal: false, canPasteLocalFiles: false, word: false, canManageUsers: false };
/** Возможность текущего пользователя (до загрузки сведений о нём - «нет»). */
export function can(cap: keyof Caps): boolean { return !!(authStore.getState().me?.capabilities || NO_CAPS)[cap]; }
export function isAdmin(): boolean { return authStore.getState().me?.user.role === 'admin'; }
export function currentUserId(): number | null { return authStore.getState().me?.user.id ?? null; }

let meWaiters: ((m: Me) => void)[] = [];
/** Промис сведений о текущем пользователе (сразу, если уже известны). */
export function whenMe(): Promise<Me> {
  const m = authStore.getState().me;
  return m ? Promise.resolve(m) : new Promise((res) => meWaiters.push(res));
}

// ------------------------------------------------------------------------------------------------ запросы
export const authApi = {
  state: (invite = '') => api.get<AuthInfo>('/api/auth/state' + (invite ? '?invite=' + encodeURIComponent(invite) : '')),
  me: () => api.get<Me>('/api/auth/me'),
  login: (login: string, password: string, remember: boolean) => api.post<Me>('/api/auth/login', { login, password, remember }),
  setup: (b: { login: string; password: string; name?: string; remember?: boolean; setupCode: string }) => api.post<Me>('/api/auth/setup', b),
  register: (b: { login: string; password: string; name?: string; invite?: string; ref?: string; remember?: boolean }) => api.post<Me>('/api/auth/register', b),
  changePassword: (old: string, nw: string) => api.post<Me>('/api/auth/change-password', { old, new: nw }),
  resetRequest: (login: string) => api.post<{ ok: boolean; contact: string }>('/api/auth/reset/request', { login }),
  resetConfirm: (login: string, code: string, password: string) => api.post<{ ok: boolean }>('/api/auth/reset/confirm', { login, code, password }),
  logout: () => api.post<{ ok: boolean }>('/api/auth/logout', {}),
  logoutOthers: () => api.post<{ revoked: number }>('/api/auth/logout-others', {}),
  sessions: () => api.get<{ sessions: SessionRow[] }>('/api/auth/sessions'),
  revokeSession: (id: string) => api.post<{ revoked: number }>('/api/auth/sessions/revoke', { id }),
  jobs: () => api.get<{ jobs: { id: string; kind: string; project: string; status: string; progress: string; queue: number | null }[] }>('/api/jobs'),
  cancelJob: (id: string) => api.post<{ cancelled: boolean }>(`/api/jobs/${encodeURIComponent(id)}/cancel`, {}),
};

// ------------------------------------------------------------------------------------------------ применение me
/** Запомнить сведения о пользователе: токен CSRF, возможности (скрыть «силы компьютера-сервера»). */
export function applyMe(me: Me) {
  setApiToken(me.csrf);
  set({ me });
  const w = meWaiters; meWaiters = [];
  w.forEach((f) => f(me));
  // «Открыть в Word», «Открыть папку» - только администратору на компьютере-сервере: остальным команд нет
  if (!me.capabilities.canOpenWord) registry.commands.remove('file.openWord');
  if (!me.capabilities.canReveal) registry.commands.remove('file.reveal');
}

let exposeApp: (() => void) | null = null;
let exposed = false;
function enterApp() {
  if (location.pathname !== '/') history.replaceState(null, '', '/' + location.hash);
  set({ phase: 'app', lost: null, lastPassword: '' });
  if (!exposed) { exposed = true; exposeApp?.(); }
  installHooks();
}

/** После входа, регистрации, первичной настройки или смены пароля. */
export function signedIn(me: Me, password = '') {
  applyMe(me);
  if (me.user.mustChangePassword) { set({ phase: 'change', lastPassword: password }); return; }
  enterApp();
}

function toAnon(info: AuthInfo | null) {
  setApiToken('');
  if (location.pathname !== '/login') history.replaceState(null, '', '/login' + location.search + location.hash);
  const { invite, ref } = authStore.getState();
  let view: AnonView = 'login';
  if (info?.needsSetup) view = info.setupAllowed ? 'setup' : 'noSetup';
  else if (invite || ref) view = 'register';
  set({ phase: 'anon', view, me: null, info });
}

/** Запуск: разбор адреса (/login?logout=1, ?invite=код), состояние сервера, нужный экран. */
export async function startAuth(expose: () => void) {
  exposeApp = expose;
  const q = new URLSearchParams(location.search);
  const onLogin = location.pathname === '/login';
  set({ invite: (q.get('invite') || '').slice(0, 100), ref: (q.get('ref') || '').slice(0, 64), setupCode: (q.get('setup') || '').slice(0, 100) });
  const hadToken = !!getApiToken() && !onLogin;
  if (hadToken) enterApp();                      // сервер отдал страницу вошедшему - приложение сразу
  if (onLogin && q.get('logout')) {
    try { await authApi.logout(); } catch { /* уже вышли */ }
    setApiToken('');
    history.replaceState(null, '', '/login' + location.hash);
  }
  let info: AuthInfo;
  try {
    info = await authApi.state(authStore.getState().invite);
  } catch (e: any) {
    if (!hadToken) set({ phase: 'fatal', error: e?.message || String(e) });
    return;
  }
  set({ info });
  if (info.user) {
    if (authStore.getState().phase === 'app') {
      applyMe(info.user);
      if (info.user.user.mustChangePassword) set({ phase: 'change' });
    } else signedIn(info.user);
  } else {
    toAnon(info);
  }
}

/** Выход: сохранить открытый отчёт, завершить сессию, страница входа (полная перезагрузка - чистое состояние). */
export async function logout(beforeLeave?: () => Promise<void>) {
  try { await beforeLeave?.(); } catch { /* сохранение не удалось - копия осталась в браузере */ }
  try { await authApi.logout(); } catch { /* сессии уже нет */ }
  location.replace('/login');
}

/** Показать экран без входа (из формы входа - регистрация и обратно). */
export function showView(view: AnonView) { set({ view }); }

/** Заново узнать состояние сервера (после ошибки «приглашение недействительно» и т. п.). */
export async function reloadInfo() {
  try { set({ info: await authApi.state(authStore.getState().invite) }); } catch { /* останется прежнее */ }
}

// ------------------------------------------------------------------------------------------------ потеря сессии
let waiters: ((ok: boolean) => void)[] = [];
const lostListeners = new Set<() => void>();
/** Вызывается при потере сессии (сохранить копию несохранённого в браузере). */
export function onSessionLost(fn: () => void) { lostListeners.add(fn); return () => { lostListeners.delete(fn); }; }

function sessionLost(e?: ApiError): Promise<boolean> {
  const s = authStore.getState();
  if (s.phase !== 'app') return Promise.resolve(false);
  if (!s.lost) {
    set({ lost: { at: Date.now(), reason: e?.data?.reason || '' } });
    lostListeners.forEach((f) => { try { f(); } catch (e) { console.error(e); } });
  }
  return new Promise((res) => waiters.push(res));
}

/** Повторный вход удался: новый токен, запросы, ждавшие входа, повторяются (сохранение текста в том числе). */
export function relogged(me: Me) {
  applyMe(me);
  set({ lost: null, phase: 'app' });
  const w = waiters; waiters = [];
  w.forEach((f) => f(true));
}

let hooked = false;
let lastCheck = 0;
function installHooks() {
  if (hooked) return;
  hooked = true;
  authHooks.relogin = (e) => sessionLost(e);
  authHooks.refresh = async () => {
    const old = getApiToken();
    try {
      const me = await api.get<Me>('/api/auth/me');
      applyMe(me);
      return me.csrf !== old;
    } catch { return false; }
  };
  authHooks.passwordChange = () => { if (authStore.getState().phase === 'app') set({ phase: 'change' }); };
  // сессию могли завершить (срок, блокировка, выход в другой вкладке) - проверяем при возврате к окну и раз в 2 мин
  const onFocus = () => { if (Date.now() - lastCheck > 15000) void checkSession(); };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') onFocus(); });
  window.setInterval(() => { if (document.visibilityState === 'visible') void checkSession(); }, 120000);
}

/** Проверить сессию сейчас (обновляет сведения о квоте; потеря сессии - повторный вход). */
export async function checkSession() {
  const s = authStore.getState();
  if (s.phase !== 'app' || s.lost) return;
  lastCheck = Date.now();
  try { applyMe(await api.get<Me>('/api/auth/me')); } catch (e) {
    if (!(e instanceof ApiError) || e.status === 0) return;       // нет связи - этим занимается автосохранение
  }
}

// ------------------------------------------------------------------------------------------------ чужой отчёт
/** Куда возвращаться из редактора: к «Все отчёты» администратора, если открыт чужой отчёт. */
export function backHash(): string {
  return authStore.getState().foreign ? '#/admin/projects' : '#/';
}
export function setForeign(pid: string | null, report?: any) {
  if (pid && report?.foreign && report.owner) set({ foreign: { pid, owner: report.owner } });
  else set({ foreign: null });
}

// ------------------------------------------------------------------------------------------------ форматирование
export function fmtBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return 'без ограничения';
  const f = (x: number, big: boolean) => (big ? String(Math.round(x)) : x.toFixed(1).replace(/\.0$/, '')).replace('.', ',');
  if (n >= 1024 ** 3) return f(n / 1024 ** 3, n >= 10 * 1024 ** 3) + ' ГБ';
  if (n >= 1024 ** 2) return f(n / 1024 ** 2, n >= 10 * 1024 ** 2) + ' МБ';
  if (n >= 1024) return Math.round(n / 1024) + ' КБ';
  return n + ' Б';
}
export const ROLE_NAMES: Record<string, string> = { admin: 'Администратор', user: 'Пользователь' };
export function displayName(u: { name?: string; login: string } | null | undefined): string {
  return u ? (u.name?.trim() || u.login) : '';
}
export function initials(u: { name?: string; login: string } | null | undefined): string {
  if (!u) return '?';
  const parts = (u.name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return u.login.slice(0, 2).toUpperCase();
}
/** Стабильный цвет «аватара» по логину (оттенок; насыщенность и яркость - из темы). */
export function hueOf(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}
